import { _lcs, locale } from "../../i18n";

/**
 * GuestFormat — how the guest-account screens (list + detail) write people,
 * dates and stays. One place, so the table and the detail page never disagree.
 */
export class GuestFormat
{
    static esc( value )
    {
        return String( value ?? "" ).replace( /[&<>"']/g, ( c ) =>
            ( { "&" : "&amp;", "<" : "&lt;", ">" : "&gt;", '"' : "&quot;", "'" : "&#39;" }[ c ] ) );
    }

    static initials( name )
    {
        const parts = String( name || "" ).trim().split( /[\s.]+/ ).filter( Boolean );
        if ( parts.length >= 2 )
        {
            return ( parts[ 0 ][ 0 ] + parts[ 1 ][ 0 ] ).toUpperCase();
        }
        return ( parts[ 0 ] || "?" ).slice( 0, 2 ).toUpperCase();
    }

    /** A date without the time ("12.10.2026"); "" for nothing. */
    static date( value )
    {
        const d = value ? new Date( value ) : null;
        if ( !d || isNaN( d.getTime() ) )
        {
            return "";
        }
        return d.toLocaleDateString( locale(), { day : "2-digit", month : "2-digit", year : "numeric" } );
    }

    /** Date and time ("12.10.2026, 14:05"). */
    static dateTime( value )
    {
        const d = value ? new Date( value ) : null;
        if ( !d || isNaN( d.getTime() ) )
        {
            return "";
        }
        return d.toLocaleString( locale(), {
            day : "2-digit", month : "2-digit", year : "numeric", hour : "2-digit", minute : "2-digit" } );
    }

    /**
     * A planned stay ("01.07. – 05.07.2026"). Plan dates are calendar days
     * ("YYYY-MM-DD"), not instants — read them as local noon so no time zone
     * can move them to the day before.
     */
    static stay( stay )
    {
        if ( !stay || !stay.startDate )
        {
            return "";
        }
        const start = new Date( stay.startDate + "T12:00:00" );
        const end = new Date( ( stay.endDate || stay.startDate ) + "T12:00:00" );
        const sameYear = start.getFullYear() === end.getFullYear();
        const from = start.toLocaleDateString( locale(), sameYear
            ? { day : "2-digit", month : "2-digit" }
            : { day : "2-digit", month : "2-digit", year : "numeric" } );
        const to = end.toLocaleDateString( locale(), { day : "2-digit", month : "2-digit", year : "numeric" } );
        return from + " – " + to;
    }

    /**
     * The account status as a pill — the same words as the other user
     * screens of this console (see ClientUser's STATUS_LABELS).
     */
    static statusPill( status )
    {
        const pill = {
            ok            : [ "ok", "Aktiv" ],
            not_validated : [ "warn", "Nicht verifiziert" ],
            blocked       : [ "err", "Gesperrt" ],
            banned        : [ "err", "Gebannt" ]
        }[ status ] || [ "neutral", status || "—" ];
        return '<span class="pill pill--' + pill[ 0 ] + '">' + GuestFormat.esc( _lcs( pill[ 1 ] ) ) + '</span>';
    }
}
